"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useRef } from "react";
import { useAtom } from "jotai";
import Image from "next/image";
import {
  templateDeploymentMessageIndexAtom,
  templateDeploymentProgressAtom,
} from "@/store/editor-jotai";

const loadingMessages = [
  { text: "Initializing intelligence layer...", duration: 1500 },
  { text: "Configuring cognitive architecture...", duration: 2000 },
  { text: "Training neural pathways...", duration: 1200 },
  { text: "Synchronizing cognitive modules...", duration: 1600 },
  { text: "Optimizing decision trees...", duration: 1800 },
  { text: "Establishing synaptic connections...", duration: 1700 },
  { text: "Calibrating confidence intervals...", duration: 1800 },
  { text: "Crunching probabilistic models...", duration: 1700 },
  { text: "Compiling insights...", duration: 2000 },
  { text: "Fine-tuning the genius parameters...", duration: 1800 },
  { text: "Running final quality checks...", duration: 1500 },
  { text: "Uploading creativity modules...", duration: 1600 },
  { text: "Assessing dynamic reasoning algorithms...", duration: 1700 },
  { text: "Preparing your co-pilot...", duration: 1800 },
];

interface TemplateDeploymentLoadingProps {
  isLoading: boolean;
}

export function TemplateDeploymentLoading({
  isLoading,
}: TemplateDeploymentLoadingProps) {
  const [messageIndex, setMessageIndex] = useAtom(
    templateDeploymentMessageIndexAtom
  );
  const [progress, setProgress] = useAtom(templateDeploymentProgressAtom);

  // Track if loading was already active when component mounted (to skip initial animation)
  const wasAlreadyLoadingRef = useRef(isLoading);
  const shouldSkipInitialAnimation = wasAlreadyLoadingRef.current && isLoading;

  // Set initial random message when loading starts
  useEffect(() => {
    if (isLoading && progress === 0) {
      setMessageIndex(Math.floor(Math.random() * loadingMessages.length));
    }
  }, [isLoading, progress, setMessageIndex]);

  useEffect(() => {
    if (!isLoading) {
      // Reset state when loading is complete
      const timer = setTimeout(() => {
        setProgress(0);
      }, 500); // Small delay to let the exit animation finish
      return () => clearTimeout(timer);
    }

    // Pick random messages
    const messageTimer = setInterval(
      () => {
        setMessageIndex((prev) => {
          // Pick a random index that's different from the current one
          let nextIndex;
          do {
            nextIndex = Math.floor(Math.random() * loadingMessages.length);
          } while (nextIndex === prev && loadingMessages.length > 1);
          return nextIndex;
        });
      },
      Math.max(2400, (loadingMessages[messageIndex]?.duration ?? 1600) + 800)
    );

    // Smooth progress bar animation
    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        const increment = Math.random() * 3 + 1;
        const next = Math.min(prev + increment, 95);
        return next;
      });
    }, 200);

    return () => {
      clearInterval(messageTimer);
      clearInterval(progressTimer);
    };
  }, [isLoading, messageIndex, setMessageIndex, setProgress]);

  // Complete progress when loading finishes
  useEffect(() => {
    if (!isLoading && progress > 0) {
      setProgress(100);
    }
  }, [isLoading, progress, setProgress]);

  const currentMessage = loadingMessages[messageIndex];

  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          initial={{ opacity: shouldSkipInitialAnimation ? 1 : 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[9999] bg-background flex items-center justify-center"
        >
          {/* Main content */}
          <div className="relative z-10 flex flex-col items-center px-8 max-w-2xl">
            {/* Animated Noxus logo */}
            <motion.div
              initial={{ scale: shouldSkipInitialAnimation ? 1 : 0 }}
              animate={{ scale: 1 }}
              transition={{
                type: "spring",
                stiffness: 260,
                damping: 20,
              }}
              className="relative"
            >
              {/* Pulsing logo */}
              <motion.div
                animate={{ scale: 1.25, opacity: 1 }}
                transition={{
                  duration: 1.6,
                  repeat: Infinity,
                  repeatType: "mirror",
                  ease: "easeInOut",
                }}
                className="w-32 h-32 flex items-center justify-center"
              >
                <Image
                  src="https://storage.googleapis.com/image-storage-spot-manual/app/sidenav/noxus-o.svg"
                  alt="Noxus logo"
                  width={55}
                  height={55}
                  className="dark:invert dark:brightness-0"
                />
              </motion.div>
            </motion.div>

            {/* Message text */}
            <div className="text-center">
              <motion.h2
                initial={{ opacity: shouldSkipInitialAnimation ? 1 : 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: shouldSkipInitialAnimation ? 0 : 0.5 }}
                className="text-lg font-semibold text-primary mb-2"
              >
                Setting up your template components
              </motion.h2>
              <AnimatePresence mode="wait">
                <motion.p
                  key={messageIndex}
                  initial={{
                    opacity: shouldSkipInitialAnimation ? 0 : 0,
                  }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                  className="text-sm text-muted-foreground font-regular leading-normal"
                >
                  {currentMessage.text}
                </motion.p>
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
